import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import Hidden from "@insite/mobius/Hidden";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";


const enum fields {
    image = "image",
    description = "description",
    link = "link"
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.image]: string;
        [fields.description]: string;
        [fields.link]: LinkFieldValue;
    };
    extendedStyles?: BannerStyles;
}

export interface BannerStyles {
    overlayWrapperStyles?: InjectableCss;
    titleStyles?: InjectableCss;
}

const mapStateToProps = (state: ApplicationState) => ({
    pageTitle: getCurrentPage(state).fields.title,
});

type Props = OwnProps & WidgetProps & ReturnType<typeof mapStateToProps>;

export interface BlocksStyles {
    container?: GridContainerProps;
    wrapper?: InjectableCss;
    linkStyle?: LinkPresentationProps;
    title?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
    descriptionWrapper?: InjectableCss;
}

export const blocksStyles: BlocksStyles = {
    wrapper: {
        css: css`
            padding: 70px 50px;
            height: 344px;
        `
    },
    description: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 30px;
                        `,
                        css`
                            padding: 30px;
                        `,
                        css`
                            padding: 30px;
                        `,
                        css`
                            padding-right: 30px;
                        `,
                        css`
                            padding-right: 30px;
                        `,
                    ],
                    "max",
                )}
        `
    },
    linkStyle: {
        css: css`
            font-weight: 600;
            display: block;
        `
    },
    descriptionWrapper: {
        css: css`
            position: relative;
            top: -210px;
        `
    },
}

const styles = blocksStyles;

const CareersCustomImageDescriptionRow: React.FunctionComponent<Props> = ({ fields }: Props) => {
    const link = useGetLink(fields.link);

    return (
        <StyledWrapper className="careers-main-image-description-block" {...styles.wrapper}>
            <Hidden below="lg">
                <Link href={link.url} {...styles.linkStyle}>
                    <StyledWrapper className="">
                        <LazyImage
                            src="/UserFiles/Images/kyle.png"
                            altText=""
                            title=""
                            />
                        <StyledWrapper {...styles.descriptionWrapper}>
                            <Typography {...styles.description}>
                                {parse(fields.description, parserOptions)}
                            </Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                </Link>
            </Hidden>
            <Hidden above="md">
                <Link href={link.url} {...styles.linkStyle}>
                    <StyledWrapper className="">
                        <StyledWrapper {...{
                            css: css`
                                background-color: #00539B;
                            `
                        }}>
                            <Typography {...styles.description}>
                                {parse(fields.description, parserOptions)}
                            </Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                </Link>
            </Hidden>
        </StyledWrapper>
    )
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CareersCustomImageDescriptionRow),
    definition: {
        group: "Basic",
        displayName: "Careers Main page Image Description",
        fieldDefinitions: [
            {
                fieldType: "Translatable",
                name: "image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: "description",
                editorTemplate: "RichTextField",
                displayName: "Description",
                defaultValue: "",
                extendedConfig: { height: 100 },
            },
            {
                fieldType: "Translatable",
                name: "link",
                displayName: "Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
        ],
    },
};

export default widgetModule;